import { FC, useMemo } from 'react';

import { HotColumn } from '@handsontable/react';
import { IList } from 'core/API/listApi';

import { MTopPageHeader, translateHeaders } from './helpers/HeadersList';
import { requireTitle } from './helpers/utils';
import Grid from '../Grid';
import { listValidator, numberValidator } from '../Validators';
import { AutoCompleteRenderer } from './helpers/customRenderers';
import { IRetailer } from 'shared/types';
import { keyBy, keys } from 'lodash';

export const MTopPage: FC<{
    list: IList;
    toolId: number;
    retailers?: string[];
    retailersData?: IRetailer[];
    useIds?: boolean;
    topType?: 'string' | 'number'
}> = ({ list, toolId, retailers, retailersData, useIds, topType = 'string' }) => {
    const translatedHeaders = translateHeaders(MTopPageHeader);

    const retailersByName = retailersData && keyBy(retailersData, 'name');
    const retailersById = retailersData && keyBy(retailersData, 'id');

    const retailerName = (retailers || keys(retailersByName)).sort();

    const afterValidate = (listToValidate: IList) => {
        const newData = [...listToValidate.data].map((row) => {
            const newRow = [...row];

            if (newRow[0] !== null && useIds) {
                newRow[0] = retailersByName?.[`${newRow[0]}`]?.id ?? '';
            }

            if (topType === 'number' && newRow[1]) {
                newRow[1] = Number(newRow[1]);
            }

            return newRow;
        });

        return { ...listToValidate, data: newData };
    };

    const formatList = (list: IList) => {
        const newData = [...list.data].map((row) => {
            const newRow = [...row];

            if (newRow[0] !== null && useIds) {
                newRow[0] = retailersById?.[`${newRow[0]}`]?.name ?? '';
            }
            if (topType === 'number' && newRow[1])
                newRow[1] = !row[1] ? null : Number(row[1]);

            return newRow;
        });

        return { ...list, data: newData };
    };

    const memoList = useMemo(() => formatList(list), [list.data, list.name]);

    return (
        <Grid toolId={toolId} headers={MTopPageHeader} list={memoList} afterValidate={afterValidate}>
            <HotColumn
                title={requireTitle(translatedHeaders[0])}
                validator={listValidator(retailerName)}
                source={retailerName}
                type="autocomplete"
            >
                <AutoCompleteRenderer list={retailerName} hot-renderer />
            </HotColumn>

            <HotColumn
                type="text"
                validator={numberValidator(false, { min: 1 })}
                title={requireTitle(translatedHeaders[1])}
            />
        </Grid>
    );
};
